import { FiTag, FiClock, FiMinus, FiPlus } from 'react-icons/fi';
import type { EventTicket } from '../../api/events';
import { getTicketSalesStatus } from '../../utils/eventTickets';
import { formatDate, formatTime, formatDateTimeLong } from '../../utils/eventDate';

interface TicketOptionCardProps {
  ticket: EventTicket;
  quantity: number;
  onAdd: (ticketId: number) => void;
  onRemove: (ticketId: number) => void;
  timezone?: string;
  disabled?: boolean;
}

export function TicketOptionCard({
  ticket,
  quantity,
  onAdd,
  onRemove,
  timezone,
  disabled,
}: TicketOptionCardProps) {
  const salesStatus = getTicketSalesStatus(ticket);
  const isSalesBlocked = salesStatus !== 'available';
  const isAddDisabled = disabled || isSalesBlocked || ticket.id === undefined;
  const salesMessage =
    salesStatus === 'not_started' && ticket.start_at
      ? `On sale from ${formatDate(ticket.start_at, timezone, true)} at ${formatTime(ticket.start_at, timezone, true)}`
      : salesStatus === 'ended'
        ? ticket.expired_at
          ? `Sales ended ${formatDateTimeLong(ticket.expired_at, timezone, true)}`
          : 'Ticket sales have ended'
        : null;

  const featureLines = (ticket.description ?? '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  return (
    <div className="relative bg-zinc-900/60 border border-white/8 rounded-2xl pl-5 pr-4 py-4">
      <div className="absolute left-1.5 top-3 bottom-3 w-1 rounded-full bg-(--event-accent)" />

      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center shrink-0">
          <FiTag className="text-zinc-300" size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-white font-semibold text-[16px]">
            {ticket.ticket_type || 'General Admission'}
          </p>
          <p className="text-[color-mix(in_oklab,var(--event-accent)_75%,white)] font-bold text-[15px] mt-0.5">
            {ticket.price === 0 ? 'Free' : `$${ticket.price.toLocaleString()}`}
          </p>
          {featureLines.length > 0 && (
            <ul className="mt-2 space-y-1">
              {featureLines.map((line, idx) => (
                <li key={idx} className="text-zinc-300 text-[13px] leading-relaxed">
                  {line}
                </li>
              ))}
            </ul>
          )}
          {salesMessage && (
            <div className="flex items-center gap-1.5 text-zinc-400 text-[12px] mt-2">
              <FiClock size={12} className="shrink-0" />
              <span>{salesMessage}</span>
            </div>
          )}
        </div>
      </div>

      <div className="mt-4">
        {quantity > 0 ? (
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={() => ticket.id !== undefined && onRemove(ticket.id)}
              disabled={disabled}
              className="w-8 h-8 rounded-full bg-(--event-accent) text-(--event-accent-fg) flex items-center justify-center hover:brightness-110 active:brightness-95 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiMinus size={13} />
            </button>
            <span className="text-white font-semibold text-[14px] min-w-5 text-center">
              {quantity}
            </span>
            <button
              type="button"
              onClick={() => ticket.id !== undefined && onAdd(ticket.id)}
              disabled={isAddDisabled}
              className="w-8 h-8 rounded-full bg-(--event-accent) text-(--event-accent-fg) flex items-center justify-center hover:brightness-110 active:brightness-95 transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiPlus size={13} />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => ticket.id !== undefined && onAdd(ticket.id)}
            disabled={isAddDisabled}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-(--event-accent) text-(--event-accent-fg) hover:brightness-110 active:brightness-95 text-[13px] font-semibold transition-[filter] disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            <FiPlus size={13} />
            Add Ticket
          </button>
        )}
      </div>
    </div>
  );
}
